import { toast } from 'sonner';

interface DownloadAudioOptions {
  url: string;
  filename?: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Downloads an audio file with proper CORS handling and fallback mechanisms
 * @param options Download options including URL and optional filename
 */
export async function downloadAudio({
  url,
  filename = 'audio.mp3',
  onError,
  onSuccess,
}: DownloadAudioOptions): Promise<void> {
  if (!url) {
    const error = new Error('No URL provided for download');
    onError?.(error);
    return;
  }

  try {
    // Method 1: Try to fetch the file with CORS handling
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'same-origin', // Authenticate app downloads; omit cookies on CDN requests.
      headers: {
        Accept: 'audio/*,*/*', // Accept any audio format
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    // Verify the blob is valid
    if (!blob || blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }

    // Create object URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = blobUrl;
    link.download = filename;

    document.body.appendChild(link);

    // Use setTimeout to ensure the element is in the DOM
    setTimeout(() => {
      link.click();

      // Clean up with a delay to ensure download starts
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(link);
        onSuccess?.();
      }, 100);
    }, 0);
  } catch (error) {
    console.error('Primary download method failed:', error);

    // Method 2: Fallback to direct link download
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);

      // Use setTimeout to ensure element is in DOM before clicking
      setTimeout(() => {
        link.click();

        // Delay removal and success callback to ensure download starts
        setTimeout(() => {
          document.body.removeChild(link);
          onSuccess?.();
        }, 100);
      }, 0);

      // Show info message for fallback method
      toast.info("Download started. If it doesn't begin, please try again.");
    } catch (fallbackError) {
      console.error('Fallback download method also failed:', fallbackError);
      const finalError = new Error(
        'Unable to download audio. Please try again later.',
      );
      onError?.(finalError);
      toast.error(finalError.message);
      return;
    }
  }
}

/**
 * Generates a filename for audio downloads
 * @param baseName Base name for the file
 * @param extension File extension (default: .mp3)
 * @returns Formatted filename with timestamp
 */
export function generateAudioFilename(
  baseName: string,
  extension: string = '.mp3',
): string {
  const timestamp = new Date().toISOString().split('T')[0];
  const sanitizedName = baseName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  return `${sanitizedName}_${timestamp}${extension.startsWith('.') ? extension : '.' + extension}`;
}

/**
 * Checks if a URL supports CORS by making a HEAD request
 * @param url URL to check
 * @returns Promise<boolean> indicating if CORS is supported
 */
export async function checkCORSSupport(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit',
    });
    return response.ok;
  } catch {
    return false;
  }
}
